import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import {
  DataTable,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { CalendarDays, Download, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type {
  InstallmentItem,
  PolicyDetailsResponse,
} from '../../../../services/policies/types';
import { SelectFilterField } from '../../../../shared/ui';
import { toaster } from '../../../../shared/ui/toaster.component';
import { PremiumCard } from '../../../proposals/components/proposal-steps/premium-card.component';
import { useInstallmentPdf, useInstallments } from '../../hooks';
import type { BrokerWriteGuard } from '../../../brokers/utils/broker-status.util';
import { isInactiveBrokerMessage } from '../../../../shared/utils/inactive-broker-toast.util';

type CapitalGlobalBillingTabProps = {
  data: PolicyDetailsResponse;
  product: string;
  proposalNumber: string;
  status: string;
  /** Corretora inativa: "Baixar boleto" travado (a Api barra com 403). */
  downloadGuard?: BrokerWriteGuard;
  /**
   * Card de prêmio no lugar do padrão (anual, do InsureMO). O Vida PME reusa
   * esta aba — mesma cobrança por subestipulante — com o card MENSAL da aba
   * Apólice.
   */
  premiumCard?: ReactNode;
};

/** Chave da parcela: com subestipulantes o nº se repete (cada sub tem a sua parcela 1). */
function installmentKey(row: InstallmentItem): string {
  return `${row.subStipulatorNumber ?? 'main'}-${row.number}`;
}

const EM_DASH = '—';
const MAIN_STIPULATOR = '__main__';

const statusConfig: Record<
  InstallmentItem['status'],
  { label: string; colorScheme: 'lightGreen' | 'error' | 'purple' | 'gray' }
> = {
  paid: { label: 'Quitado', colorScheme: 'lightGreen' },
  overdue: { label: 'Vencida', colorScheme: 'error' },
  pending: { label: 'A vencer', colorScheme: 'purple' },
  cancelled: { label: 'Cancelado', colorScheme: 'gray' },
};

const statusFilterOptions = [
  { label: 'Todos os status', value: '' },
  { label: 'Quitado', value: 'paid' },
  { label: 'A vencer', value: 'pending' },
  { label: 'Cancelado', value: 'cancelled' },
];

/** Considera que a parcela tem subestipulante quando há nome preenchido. */
function hasSubStipulator(row: InstallmentItem): boolean {
  return Boolean(row.subStipulatorName && row.subStipulatorName.trim());
}

type ColumnOptions = {
  onDownload: (row: InstallmentItem) => void;
  downloadingRow: string | null;
  downloadGuard?: BrokerWriteGuard;
};

function createColumns({
  onDownload,
  downloadingRow,
  downloadGuard,
}: ColumnOptions): DataTableColumn<InstallmentItem>[] {
  const text = (value: string) => (
    <span className='text-sm text-alba-gray-700'>{value}</span>
  );

  return [
    {
      accessorKey: 'number',
      header: 'Nº DA PARCELA',
      size: 110,
      cell: ({ row }) => text(String(row.original.number)),
    },
    {
      id: 'subStipulatorNumber',
      header: 'Nº DA SUB.',
      size: 100,
      cell: ({ row }) =>
        text(
          hasSubStipulator(row.original) && row.original.subStipulatorNumber != null
            ? String(row.original.subStipulatorNumber)
            : EM_DASH,
        ),
    },
    {
      id: 'subStipulatorName',
      header: 'NOME DA SUB.',
      size: 200,
      cell: ({ row }) =>
        text(hasSubStipulator(row.original) ? row.original.subStipulatorName! : EM_DASH),
    },
    {
      accessorKey: 'amount',
      header: 'VALOR',
      size: 130,
      cell: ({ row }) => text(formatCurrency(row.original.amount ?? 0)),
    },
    {
      accessorKey: 'dueDate',
      header: 'DATA DE VENCIMENTO',
      size: 180,
      cell: ({ row }) => {
        const isOverdue = row.original.status === 'overdue';
        return (
          <div
            className={`flex items-center gap-1.5 ${isOverdue ? 'text-red-500' : 'text-alba-gray-700'}`}
          >
            <CalendarDays className='h-4 w-4 shrink-0' />
            <span className='text-sm'>{formatDateToBrazil(row.original.dueDate)}</span>
          </div>
        );
      },
    },
    {
      id: 'paymentDate',
      header: 'DATA DE PAGAMENTO',
      size: 180,
      cell: ({ row }) =>
        row.original.paymentDate ? (
          <div className='flex items-center gap-1.5 text-alba-gray-700'>
            <CalendarDays className='h-4 w-4 shrink-0' />
            <span className='text-sm'>{formatDateToBrazil(row.original.paymentDate)}</span>
          </div>
        ) : (
          <span className='text-sm text-alba-gray-400'>{EM_DASH}</span>
        ),
    },
    {
      id: 'status',
      header: 'STATUS',
      size: 120,
      cell: ({ row }) => {
        const config = statusConfig[row.original.status];
        return (
          <Tag.Root variant='solid' colorScheme={config.colorScheme} size='sm'>
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      id: 'download',
      header: 'BAIXAR',
      size: 80,
      cell: ({ row }) => {
        const isLoading = downloadingRow === installmentKey(row.original);
        return (
          <button
            onClick={() => onDownload(row.original)}
            disabled={isLoading || downloadGuard?.blocked}
            className='flex cursor-pointer items-center justify-center border-none bg-transparent text-alba-gray-500 transition-colors hover:text-alba-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
            title={downloadGuard?.reason ?? 'Baixar boleto'}
          >
            {isLoading ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Download className='h-4 w-4' />
            )}
          </button>
        );
      },
    },
  ];
}

export function CapitalGlobalBillingTab({
  data,
  product,
  proposalNumber,
  status,
  downloadGuard,
  premiumCard,
}: CapitalGlobalBillingTabProps) {
  const { data: installments = [], isLoading } = useInstallments(data.policyId);
  const { mutateAsync: downloadPdf } = useInstallmentPdf();
  const [downloadingRow, setDownloadingRow] = useState<string | null>(null);
  const [stipulatorFilter, setStipulatorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const handleDownload = useCallback(
    async (row: InstallmentItem) => {
      if (downloadingRow !== null || downloadGuard?.blocked) return;

      if (!data.policyId) {
        toaster.create({
          type: 'warning',
          title: 'Boleto indisponível',
          description: 'Não foi possível baixar o boleto porque não foi localizada a apólice',
        });
        return;
      }

      setDownloadingRow(installmentKey(row));

      try {
        await downloadPdf({
          policyId: data.policyId,
          installmentNumber: row.number,
          subStipulatorNumber: row.subStipulatorNumber,
        });
      } catch (error) {
        // Corretora inativa: o aviso central (403) já apareceu.
        if (error instanceof Error && isInactiveBrokerMessage(error.message))
          return;
        toaster.create({
          type: 'error',
          title: 'Erro ao baixar boleto',
          description: 'Boleto ainda não disponível para esta parcela',
        });
      } finally {
        setDownloadingRow(null);
      }
    },
    [data.policyId, downloadPdf, downloadingRow, downloadGuard?.blocked]
  );

  const totalPremium = data.grossPremium ?? data.duePremium ?? 0;
  const liquidValue = data.rawPremium != null ? formatCurrency(data.rawPremium) : '-';
  const iofValue = data.iof ?? 0;

  // Opções do filtro de Estipulante a partir dos subestipulantes presentes.
  const stipulatorOptions = useMemo(() => {
    const options = [{ label: 'Todos os estipulantes', value: '' }];
    const hasMain = installments.some(row => !hasSubStipulator(row));
    if (hasMain) options.push({ label: 'Estipulante', value: MAIN_STIPULATOR });

    const seen = new Map<string, string>();
    for (const row of installments) {
      if (!hasSubStipulator(row) || row.subStipulatorNumber == null) continue;
      const value = String(row.subStipulatorNumber);
      if (!seen.has(value)) seen.set(value, row.subStipulatorName!.trim());
    }
    for (const [value, name] of seen) {
      options.push({ label: name, value });
    }
    return options;
  }, [installments]);

  const filtered = useMemo(() => {
    return installments.filter(row => {
      if (statusFilter && row.status !== statusFilter) return false;
      if (stipulatorFilter) {
        if (stipulatorFilter === MAIN_STIPULATOR) return !hasSubStipulator(row);
        if (String(row.subStipulatorNumber ?? '') !== stipulatorFilter) return false;
      }
      return true;
    });
  }, [installments, statusFilter, stipulatorFilter]);

  const columns = useMemo(
    () =>
      createColumns({
        onDownload: handleDownload,
        downloadingRow,
        downloadGuard,
      }),
    [handleDownload, downloadingRow, downloadGuard]
  );

  return (
    <div className='mt-6 flex flex-col gap-6'>
      {premiumCard ?? (
        <PremiumCard
          totalValue={formatCurrency(totalPremium)}
          liquidValue={liquidValue}
          interestPercentage=''
          interestValue=''
          iofValue={formatCurrency(iofValue)}
          product={product}
          proposalNumber={proposalNumber}
          proposalLabel='Número da Apólice'
          status={status}
          showPisCofins={false}
        />
      )}

      {/* Filtros */}
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
        <SelectFilterField
          name='stipulator'
          type='string'
          items={stipulatorOptions}
          value={stipulatorFilter}
          onChange={value => setStipulatorFilter(String(value ?? ''))}
          placeholder='Estipulante'
          className='w-full sm:max-w-xs'
        />
        <SelectFilterField
          name='status'
          type='string'
          items={statusFilterOptions}
          value={statusFilter}
          onChange={value => setStatusFilter(String(value ?? ''))}
          placeholder='Status'
          className='w-full sm:max-w-xs'
        />
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-6 w-6 animate-spin text-alba-gray-400' />
        </div>
      ) : (
        <>
          <div className='hidden sm:block'>
            <DataTable
              columns={columns}
              data={filtered}
              totalItems={filtered.length}
              totalPages={1}
              pageSize={10}
            />
          </div>

          {/* Layout mobile */}
          <div className='flex flex-col gap-3 sm:hidden'>
            {filtered.length === 0 && (
              <div className='rounded-2xl border border-alba-gray-200 bg-white px-4 py-6 text-center text-sm text-alba-gray-600'>
                Nenhuma parcela encontrada.
              </div>
            )}
            {filtered.map(row => {
              const config = statusConfig[row.status];
              return (
                <div
                  key={installmentKey(row)}
                  className='rounded-2xl border border-alba-gray-200 bg-white px-4 py-3 shadow-[0px_2px_10px_0px_rgba(15,22,30,0.06)]'
                >
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-semibold text-alba-black-500'>
                      Parcela {row.number}
                    </span>
                    <Tag.Root variant='solid' colorScheme={config.colorScheme} size='sm'>
                      <Tag.Label>{config.label}</Tag.Label>
                    </Tag.Root>
                  </div>

                  <div className='mt-3 flex flex-col gap-2'>
                    <div className='flex items-center justify-between'>
                      <span className='text-xs font-medium text-alba-gray-500'>Nº da Sub.</span>
                      <span className='text-sm text-alba-gray-700'>
                        {hasSubStipulator(row) && row.subStipulatorNumber != null
                          ? row.subStipulatorNumber
                          : EM_DASH}
                      </span>
                    </div>
                    <div className='flex items-center justify-between gap-3'>
                      <span className='text-xs font-medium text-alba-gray-500'>Nome da Sub.</span>
                      <span className='truncate text-sm text-alba-gray-700'>
                        {hasSubStipulator(row) ? row.subStipulatorName : EM_DASH}
                      </span>
                    </div>
                    <div className='h-px w-full bg-alba-gray-200' />
                    <div className='flex items-center justify-between'>
                      <span className='text-xs font-medium text-alba-gray-500'>Valor</span>
                      <span className='text-sm text-alba-gray-700'>
                        {formatCurrency(row.amount ?? 0)}
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span className='text-xs font-medium text-alba-gray-500'>Vencimento</span>
                      <div className='flex items-center gap-1 text-sm text-alba-gray-700'>
                        <CalendarDays className='h-4 w-4' />
                        <span>{formatDateToBrazil(row.dueDate)}</span>
                      </div>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span className='text-xs font-medium text-alba-gray-500'>Pagamento</span>
                      <span className='text-sm text-alba-gray-700'>
                        {row.paymentDate ? formatDateToBrazil(row.paymentDate) : EM_DASH}
                      </span>
                    </div>

                    <div className='h-px w-full bg-alba-gray-200' />
                    <button
                      onClick={() => void handleDownload(row)}
                      disabled={
                        downloadingRow === installmentKey(row) || downloadGuard?.blocked
                      }
                      title={downloadGuard?.reason}
                      className='flex items-center justify-center gap-2 rounded-lg border border-alba-gray-200 py-2 text-sm font-medium text-alba-gray-700 disabled:opacity-60'
                    >
                      {downloadingRow === installmentKey(row) ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <Download className='h-4 w-4' />
                      )}
                      Baixar boleto
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
